import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Smartphone, Monitor, Tablet, AlertTriangle, ArrowRight, LogOut, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { API_URL as API } from '@/utils/apiConfig';


export default function DeviceLimitModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { devices, replaceDevice, dismissDeviceLimit } = useSubscription();
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [replacing, setReplacing] = useState(false);

  const handleReplace = async () => {
    if (!selectedDeviceId) {
      toast.error(t('device_limit.select_error'));
      return;
    }
    try {
      setReplacing(true);
      const result = await replaceDevice(selectedDeviceId);
      toast.success(t('device_limit.replace_success', { name: result.removed_device }));
    } catch {
      toast.error(t('device_limit.replace_error'));
    } finally {
      setReplacing(false);
    }
  };

  const handleGoBack = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore
    }
    dismissDeviceLimit();
    navigate('/login');
  };

  const sortedDevices = [...(devices.list || [])].sort((a, b) => {
    const aTime = a.last_active ? new Date(a.last_active).getTime() : 0;
    const bTime = b.last_active ? new Date(b.last_active).getTime() : 0;
    return bTime - aTime;
  });

  const selectedDevice = sortedDevices.find(d => d.device_id === selectedDeviceId);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="device-limit-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-3xl p-6 shadow-2xl animate-in zoom-in-95 fade-in duration-300"
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--glass-border)',
        }}
      >
        <div className="flex flex-col items-center text-center mb-5">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'rgba(245, 158, 11, 0.15)' }}
          >
            <AlertTriangle className="h-7 w-7 text-amber-400" />
          </div>
          <h2 className="text-xl font-bold mb-1.5" style={{ color: 'var(--theme-text)' }}>
            {t('device_limit.title')}
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('device_limit.description', { max: devices.max })}
          </p>
        </div>

        <div className="space-y-2 mb-5 max-h-52 overflow-y-auto" data-testid="device-limit-device-list">
          {sortedDevices.map((device) => {
            const PlatformIcon = device.platform === 'ios' || device.platform === 'android'
              ? Smartphone
              : device.platform === 'tablet' ? Tablet : Monitor;
            const isSelected = selectedDeviceId === device.device_id;

            return (
              <button
                key={device.device_id}
                onClick={() => setSelectedDeviceId(device.device_id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${isSelected ? 'ring-2' : ''}`}
                style={{
                  background: isSelected ? 'var(--glass-bg-active)' : 'var(--glass-bg)',
                  borderColor: isSelected ? 'var(--theme-accent)' : 'transparent',
                  '--tw-ring-color': 'var(--theme-accent)',
                }}
                data-testid={`device-select-${device.device_id}`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: isSelected ? 'var(--theme-accent)' : 'var(--glass-bg-active)' }}>
                  {isSelected ? (
                    <Check className="h-4 w-4 text-white" />
                  ) : (
                    <PlatformIcon className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
                    {device.device_name || t('device_limit.unknown_device')}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--theme-textSecondary)' }}>
                    {device.platform?.charAt(0).toUpperCase() + device.platform?.slice(1) || 'Web'}
                    {device.last_active && (
                      <> &middot; {t('device_limit.last_active')} {new Date(device.last_active).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="space-y-2.5">
          <Button
            onClick={handleReplace}
            disabled={replacing || !selectedDeviceId}
            className="w-full rounded-xl h-11 font-medium gap-2 disabled:opacity-40"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid="device-limit-continue"
          >
            {replacing ? (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <ArrowRight className="h-4 w-4" />
                {selectedDevice
                  ? t('device_limit.replace', { name: selectedDevice.device_name })
                  : t('device_limit.select_device')}
              </>
            )}
          </Button>

          <Button
            onClick={handleGoBack}
            variant="outline"
            className="w-full rounded-xl h-11 font-medium gap-2 border-border text-foreground hover:bg-muted"
            data-testid="device-limit-goback"
          >
            <LogOut className="h-4 w-4" />
            {t('device_limit.sign_out')}
          </Button>

          <p className="text-[10px] text-center leading-relaxed" style={{ color: 'var(--theme-textSecondary)', opacity: 0.6 }}>
            {t('device_limit.hint')}
          </p>
        </div>
      </div>
    </div>
  );
}
